import { createElement, lazy, type ComponentType } from 'react';

/**
 * Public pages that can be loaded before the app's first render.
 *
 * A static host serves each public page as prerendered HTML, and the app then renders
 * over it (createRoot, not hydration). With a plain React.lazy page, that first render
 * is the Suspense spinner, so the finished page was replaced by a spinner and then by
 * the page again -- the footer and the content jumped on every load. client-main.tsx
 * calls preloadPage() for the current address before mounting, so the first render is
 * already the page itself.
 */
const preloaders: [RegExp, () => Promise<unknown>][] = [];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyPage(factory: () => Promise<{ default: ComponentType<any> }>, route: RegExp): ComponentType<any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let loaded: ComponentType<any> | null = null;
  const load = () =>
    factory().then((module) => {
      loaded = module.default;
      return module;
    });
  const LazyPage = lazy(load);
  preloaders.push([route, load]);
  // Once loaded, render the page directly -- no Suspense boundary to fall back to.
  const Page = (props: Record<string, unknown>) => createElement(loaded ?? LazyPage, props);
  return Page;
}

/** Loads the page registered for `pathname`, if any, so the first render needn't suspend. */
export function preloadPage(pathname: string): Promise<unknown> {
  const path = pathname.replace(/\/+$/, '') || '/';
  const match = preloaders.find(([route]) => route.test(path));
  return match ? match[1]() : Promise.resolve();
}
